import { Button, Input, Modal, Select, Table, Tag, Typography, notification } from 'antd';
import axiosClient from 'api/axiosClient';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  getCandidateSourceLabel,
  getCandidateSourceTagColors,
} from 'features/recruitment/candidates/utils/candidateSource';

const { Text } = Typography;
const { Option } = Select;

interface Props {
  visible: boolean;
  jobId?: string;
  onCancel: () => void;
  onAdded: () => void;
}

const AddCandidatesModal = ({ visible, jobId, onCancel, onAdded }: Props) => {
  const { t } = useTranslation(['recruitment', 'action']);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [searchName, setSearchName] = useState('');
  const [source, setSource] = useState<string>();
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [positionName, setPositionName] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadCandidates = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    try {
      const response: any = await axiosClient.post('/recruitment/candidate/management', {
        limit: 10,
        offset: page,
        searchName: searchName.trim() || undefined,
        poolOnly: true,
        jobId,
        filter: {
          source: source || undefined,
          positionName: positionName.trim() || undefined,
        },
      });
      const result = response?.data?.result || [];
      setRows(
        result.filter(
          (item: any, index: number, list: any[]) =>
            list.findIndex((candidate: any) => candidate.id === item.id) === index,
        ),
      );
      setTotal(Number(response?.data?.totalResult || 0));
    } catch (error) {
      notification.error({ message: t('recruitment:candidate_pool_load_error') });
    } finally {
      setLoading(false);
    }
  }, [jobId, page, positionName, searchName, source, t, visible]);

  useEffect(() => {
    if (!visible) return;
    setSelectedIds([]);
    setSearchName('');
    setSource(undefined);
    setPositionName('');
    setPage(1);
    axiosClient
      .get('/recruitment/candidate/sources')
      .then((response: any) => setSourceOptions(response?.data || []))
      .catch(() => setSourceOptions([]));
  }, [visible]);

  useEffect(() => {
    const timer = window.setTimeout(loadCandidates, 250);
    return () => window.clearTimeout(timer);
  }, [loadCandidates]);

  const columns = useMemo(
    () => [
      {
        title: t('recruitment:candidate_name'),
        dataIndex: 'candidateName',
        width: 180,
      },
      { title: t('recruitment:email'), dataIndex: 'email', width: 210 },
      {
        title: t('recruitment:candidate_source'),
        dataIndex: 'source',
        width: 120,
        render: (value: string) => {
          const label = getCandidateSourceLabel(value);
          const colors = getCandidateSourceTagColors(value);
          return (
            <Tag
              style={{
                color: colors.text,
                background: colors.background,
                borderColor: colors.border,
              }}
            >
              {label}
            </Tag>
          );
        },
      },
      {
        title: t('recruitment:applied_position'),
        dataIndex: 'positionName',
        width: 210,
        render: (value: string) => value || '—',
      },
      {
        title: t('recruitment:applied_date'),
        dataIndex: 'appliedDate',
        width: 120,
        render: (value: string) => (value ? moment(value).format('DD/MM/YYYY') : '—'),
      },
    ],
    [t],
  );

  const handleAdd = async () => {
    if (!jobId || !selectedIds.length) return;
    setSubmitting(true);
    try {
      const response: any = await axiosClient.post('/recruitment/candidate/pool/add-to-job', {
        jobId,
        candidateIds: selectedIds,
      });
      const result = response?.data || {};
      notification.success({
        message: t('recruitment:candidate_pool_add_result', {
          added: result.added || 0,
          failed: result.failed || 0,
        }),
      });
      setSelectedIds([]);
      await loadCandidates();
      if (result.added > 0) onAdded();
    } catch (error) {
      notification.error({ message: t('recruitment:candidate_pool_add_error') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      width={1060}
      centered
      maskClosable={false}
      title={t('recruitment:candidate_pool_add_title')}
      onCancel={submitting ? undefined : onCancel}
      footer={null}
      destroyOnClose
    >
      <Description type="secondary">{t('recruitment:candidate_pool_add_description')}</Description>
      <FilterRow>
        <Input
          value={searchName}
          allowClear
          placeholder={t('recruitment:candidate_pool_search') as string}
          onChange={(event) => {
            setSearchName(event.target.value);
            setPage(1);
          }}
        />
        <Select
          value={source}
          allowClear
          placeholder={t('recruitment:candidate_source') as string}
          onChange={(value) => {
            setSource(value);
            setPage(1);
          }}
        >
          {sourceOptions.map((item) => (
            <Option key={item} value={item}>
              {item}
            </Option>
          ))}
        </Select>
        <Input
          value={positionName}
          allowClear
          placeholder={t('recruitment:applied_position') as string}
          onChange={(event) => {
            setPositionName(event.target.value);
            setPage(1);
          }}
        />
      </FilterRow>

      <SelectionBar>
        <Text>{t('recruitment:candidate_pool_selected', { count: selectedIds.length })}</Text>
        {selectedIds.length > 0 && (
          <ClearButton type="button" onClick={() => setSelectedIds([])}>
            {t('action:clear')}
          </ClearButton>
        )}
      </SelectionBar>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 900, y: 360 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          preserveSelectedRowKeys: true,
          onChange: setSelectedIds,
        }}
        pagination={{
          current: page,
          pageSize: 10,
          total,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />

      <Footer>
        <Button disabled={submitting} onClick={onCancel}>
          {t('action:cancel')}
        </Button>
        <PrimaryButton
          loading={submitting}
          disabled={!selectedIds.length || !jobId}
          onClick={handleAdd}
        >
          {t('recruitment:candidate_pool_add_count', { count: selectedIds.length })}
        </PrimaryButton>
      </Footer>
    </Modal>
  );
};

const Description = styled(Text)`
  display: block;
  margin-bottom: 18px;
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px minmax(220px, 0.8fr);
  gap: 10px;
  margin-bottom: 12px;

  .ant-select {
    width: 100%;
  }
`;

const SelectionBar = styled.div`
  min-height: 38px;
  padding: 8px 12px;
  margin-bottom: 10px;
  background: #fff7f3;
  border: 1px solid #f4d4c7;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ClearButton = styled.button`
  border: 0;
  background: transparent;
  color: #f15a29;
  cursor: pointer;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #eeeeee;
`;

const PrimaryButton = styled(Button)`
  && {
    color: #ffffff;
    border-color: #f15a29;
    background: #f15a29;
  }
`;

export default AddCandidatesModal;
